import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/contexts/AuthContext';
import { CreditCard, CurrencyDollar, ArrowCircleRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/lib/api';

function statusClass(s) {
  const map = { completed: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', processing: 'bg-blue-500/10 text-blue-400 border-blue-500/20', pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20', failed: 'bg-red-500/10 text-red-400 border-red-500/20' };
  return map[s] || '';
}

export default function PayoutManagement() {
  const { user } = useAuth();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    api.get('/payments/history').then(r => setPayouts(r.data)).catch(console.error).finally(() => setLoading(false));

    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.info('Payment status check complete. Check your email for confirmation.');
      return;
    }
    setCheckingPayment(true);
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === 'paid') {
        toast.success('Payment successful!');
        setCheckingPayment(false);
        api.get('/payments/history').then(r => setPayouts(r.data));
        return;
      }
      if (data.status === 'expired') {
        toast.error('Payment session expired.');
        setCheckingPayment(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch {
      setCheckingPayment(false);
    }
  };

  const requestPayout = async () => {
    try {
      const { data } = await api.post('/payments/checkout', { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate payout');
    }
  };

  return (
    <div className="space-y-6" data-testid="payout-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <CreditCard size={32} className="text-blue-400" weight="duotone" />
            Payout Management
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Track and manage your payouts</p>
        </div>
        {user?.role === 'affiliate' && (
          <Button data-testid="request-payout-btn" onClick={requestPayout} className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
            <ArrowCircleRight size={16} /> Request Payout
          </Button>
        )}
      </div>

      {checkingPayment && (
        <Card className="bg-blue-500/5 border-blue-500/20">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-blue-400">Verifying payment status...</span>
          </CardContent>
        </Card>
      )}

      <div className="grid sm:grid-cols-3 gap-4">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Balance</p>
            <p className="text-2xl font-bold font-heading text-emerald-400 mt-1">${(user?.balance || 0).toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Total Payouts</p>
            <p className="text-2xl font-bold font-heading text-blue-400 mt-1">{payouts.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Total Paid</p>
            <p className="text-2xl font-bold font-heading text-emerald-400 mt-1">
              ${payouts.filter(p => p.status === 'completed').reduce((sum, p) => sum + (p.amount || 0), 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg text-white">Payout History</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">ID</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Affiliate</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Amount</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Method</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payouts.map((p, i) => (
                  <TableRow key={i} className="border-zinc-800/50" data-testid={`payout-history-${i}`}>
                    <TableCell className="text-zinc-500 font-mono text-xs">{p.payout_id}</TableCell>
                    <TableCell className="text-zinc-200">{p.affiliate_name}</TableCell>
                    <TableCell className="text-emerald-400 font-mono">${(p.amount || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-zinc-300 text-sm">{p.method}</TableCell>
                    <TableCell><Badge className={`${statusClass(p.status)} text-[10px]`}>{p.status}</Badge></TableCell>
                    <TableCell className="text-zinc-500 text-xs">{p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}</TableCell>
                  </TableRow>
                ))}
                {payouts.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-8">No payout history yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
